"use client";

import React, { useEffect } from "react";
import Script from "next/script";
import { usePathname } from "next/navigation";

declare global {
  interface Window {
    __legacyBridgeInit?: () => void;
    __legacyBridgePageInit?: Record<string, () => void>;
  }
}

function runBridgeInitializers(pageKey: string | undefined) {
  window.__legacyBridgeInit?.();
  if (pageKey) window.__legacyBridgePageInit?.[pageKey]?.();
}

type RuntimeModule = typeof import("./legacy-bridge-runtime");

// One retry for deploy-skew chunk-load failures (stale HTML referencing a
// purged chunk); after that fail quietly — same dead-runtime outcome as any
// unreachable script, but without an unhandled rejection.
function loadRuntime(): Promise<RuntimeModule | null> {
  return import("./legacy-bridge-runtime").catch(() =>
    import("./legacy-bridge-runtime").catch(() => null)
  );
}

/*
 * Initializes the shared legacy-bridge runtime, then the page-specific
 * supplement.
 *
 * The shared runtime is a bundled module (src/legacy-bridge/legacy-bridge-
 * runtime.ts) dynamic-imported inside useEffect: SSR-safe, and shipped as a
 * hashed /_next/static chunk so it works on any host with no bespoke edge
 * routes (the old root-served public/legacy-bridge-runtime.js fell through
 * to the legacy backend on www and 404ed). The effect re-runs on every
 * pathname change so App Router client navigation re-scans the fresh bridge
 * DOM, and its cleanup tears down the previous scan's listeners first.
 *
 * Page supplements (Contentful jsAsset) render only after the runtime has
 * scanned THIS route: the gate stores which pathname was ready (readyFor),
 * so on client navigation the next route's supplement cannot mount off the
 * previous route's readiness — it waits for its own effect's teardown +
 * re-init + ready cycle. The <Script> is keyed by src because next/script
 * tracks "already loaded" per component instance: an unkeyed instance
 * navigating between two pages with distinct jsAssets would never request
 * the second one. next/script executes a given src once per browser session;
 * on SPA revisits the tag may not re-run, but onReady fires on every remount
 * and re-invokes window.__legacyBridgeInit plus the supplement's keyed
 * window.__legacyBridgePageInit hook. Double invocation is safe: shared init
 * marks each .legacy-bridge scope bound, and page-specific hooks own their
 * own idempotency.
 */
export default function LegacyBridgeRuntimeLoader({
  pageKey,
  pageSrc
}: {
  pageKey?: string;
  pageSrc?: string | null;
}) {
  const pathname = usePathname();
  const [readyFor, setReadyFor] = React.useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    let teardown: (() => void) | undefined;

    loadRuntime().then((runtime) => {
      if (cancelled || !runtime) return;
      teardown = runtime.initLegacyBridge();
      if (pageKey) window.__legacyBridgePageInit?.[pageKey]?.();
      runtime.whenLegacyBridgeReady().then(() => {
        if (!cancelled) setReadyFor(pathname);
      });
    });

    return () => {
      cancelled = true;
      teardown?.();
      setReadyFor(null);
    };
  }, [pageKey, pathname]);

  if (!pageSrc || readyFor !== pathname) return null;

  return (
    <Script
      key={pageSrc}
      src={pageSrc}
      strategy="afterInteractive"
      onReady={() => {
        runBridgeInitializers(pageKey);
      }}
    />
  );
}
